import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { kpiAPI, healthAPI, aiAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Target,
  Brain,
  Sparkles,
  ArrowUpRight,
  Activity,
  Clock,
  CheckCircle,
  Award
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';

const KPI = () => {
  const { user } = useAuth();
  const [kpiData, setKpiData] = useState([]);
  const [healthData, setHealthData] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user?.employee_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [kpiRes, healthRes] = await Promise.all([
        kpiAPI.getByEmployee(user?.employee_id || '100001'),
        healthAPI.getByEmployee(user?.employee_id || '100001')
      ]);
      setKpiData(kpiRes.data);
      setHealthData(healthRes.data);
    } catch (error) {
      console.error('KPI fetch error:', error);
      toast.error('Failed to load KPI data');
    } finally {
      setLoading(false);
    }
  };

  const handleAIAnalysis = async () => {
    try {
      setAnalyzing(true);
      const response = await aiAPI.analyzeEmployee(user?.employee_id || '100001');
      setAiAnalysis(response.data);
      toast.success('AI Analysis completed!');
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Failed to generate AI analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const currentKpi = kpiData?.[0] || {
    productivity_score: 84,
    focus_score: 78,
    task_completion: 92,
    training_score: 75,
    attendance_score: 98,
    wellness_score: 82,
    overall_score: 84.7
  };

  const radarData = [
    { metric: 'Productivity', value: currentKpi.productivity_score, fullMark: 100 },
    { metric: 'Focus', value: currentKpi.focus_score, fullMark: 100 },
    { metric: 'Tasks', value: currentKpi.task_completion, fullMark: 100 },
    { metric: 'Training', value: currentKpi.training_score, fullMark: 100 },
    { metric: 'Attendance', value: currentKpi.attendance_score, fullMark: 100 },
    { metric: 'Wellness', value: currentKpi.wellness_score, fullMark: 100 },
  ];

  const trendData = kpiData?.slice(0, 14).reverse().map((kpi, index) => ({
    day: `Day ${index + 1}`,
    overall: kpi.overall_score,
    productivity: kpi.productivity_score,
    wellness: kpi.wellness_score
  })) || [];

  const weeklyComparison = [
    { name: 'Week 1', score: 78 },
    { name: 'Week 2', score: 82 },
    { name: 'Week 3', score: 79 },
    { name: 'Week 4', score: 85 },
  ];

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 85) return { label: 'Excellent', class: 'bg-emerald-100 text-emerald-700' };
    if (score >= 70) return { label: 'Good', class: 'bg-blue-100 text-blue-700' };
    if (score >= 50) return { label: 'Average', class: 'bg-amber-100 text-amber-700' };
    return { label: 'Needs Improvement', class: 'bg-red-100 text-red-700' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading KPI data...</p>
        </div>
      </div>
    );
  }

  const badge = getScoreBadge(currentKpi.overall_score);

  return (
    <div className="space-y-6" data-testid="kpi-page">
      {/* Overall Score Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 text-white border-none">
          <CardContent className="p-8 text-center">
            <Award className="w-12 h-12 mx-auto mb-4 text-indigo-200" />
            <p className="text-indigo-100 mb-2">Overall KPI Score</p>
            <p className="text-6xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {Math.round(currentKpi.overall_score)}%
            </p>
            <Badge className="bg-white/20 text-white border-none text-sm px-4 py-1">
              {badge.label}
            </Badge>
            <div className="mt-6 flex items-center justify-center gap-2 text-indigo-100">
              <TrendingUp className="w-4 h-4" />
              <span>+5.2% from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Radar Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Performance Overview</CardTitle>
            <CardDescription>Your performance across all metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94A3B8' }} />
                  <Radar
                    name="Score"
                    dataKey="value"
                    stroke="#4F46E5"
                    fill="#4F46E5"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Productivity', value: currentKpi.productivity_score, icon: Activity, color: 'indigo' },
          { label: 'Focus', value: currentKpi.focus_score, icon: Target, color: 'violet' },
          { label: 'Tasks', value: currentKpi.task_completion, icon: CheckCircle, color: 'emerald' },
          { label: 'Training', value: currentKpi.training_score, icon: Award, color: 'blue' },
          { label: 'Attendance', value: currentKpi.attendance_score, icon: Clock, color: 'amber' },
          { label: 'Wellness', value: currentKpi.wellness_score, icon: Brain, color: 'pink' },
        ].map((metric) => (
          <Card key={metric.label} className="card-hover" data-testid={`metric-${metric.label.toLowerCase()}`}>
            <CardContent className="p-4 text-center">
              <div className={`w-10 h-10 rounded-lg bg-${metric.color}-100 flex items-center justify-center mx-auto mb-3`}>
                <metric.icon className={`w-5 h-5 text-${metric.color}-600`} />
              </div>
              <p className={`text-2xl font-bold ${getScoreColor(metric.value)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {metric.value}%
              </p>
              <p className="text-sm text-slate-500 mt-1">{metric.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Performance Trend</CardTitle>
            <CardDescription>Your KPI trend over the past 2 weeks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="overallGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} domain={[60, 100]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="overall" 
                    stroke="#4F46E5" 
                    strokeWidth={2}
                    fill="url(#overallGradient)"
                    name="Overall Score"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Weekly Comparison</CardTitle>
            <CardDescription>Performance by week this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} domain={[0, 100]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="score" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Score" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Analysis Section */}
      <Card className="bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>AI Performance Analysis</CardTitle>
                <CardDescription>Get personalized insights powered by GPT-5.2</CardDescription>
              </div>
            </div>
            <Button 
              onClick={handleAIAnalysis}
              disabled={analyzing}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
              data-testid="ai-analysis-btn"
            >
              {analyzing ? (
                <>
                  <div className="spinner w-4 h-4 mr-2 border-2 border-white/30 border-t-white" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Generate Analysis
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        {aiAnalysis && (
          <CardContent>
            <div className="bg-white rounded-lg p-6 border border-violet-100">
              <h4 className="font-semibold text-slate-900 mb-3">AI Insights</h4>
              <div className="prose prose-slate prose-sm max-w-none">
                <p className="text-slate-600 whitespace-pre-wrap">{aiAnalysis.ai_analysis}</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default KPI;
